import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { initializeMobile, isNative } from "@/utils/mobile";
import { LocationProvider } from "@/contexts/LocationContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import ExpertsPage from "@/pages/ExpertsPage";
import ExpertProfilePage from "@/pages/ExpertProfilePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import ExpertDashboardPage from "@/pages/ExpertDashboardPage";
import BookingSuccessPage from "@/pages/BookingSuccessPage";
import EnterprisePage from "@/pages/EnterprisePage";
import BecomeExpertPage from "@/pages/BecomeExpertPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import AdminForgotPasswordPage from "@/pages/AdminForgotPasswordPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ExpertEditProfilePage from "@/pages/ExpertEditProfilePage";
import ExpertOnboardingSuccessPage from "@/pages/ExpertOnboardingSuccessPage";
import VideoCallPage from "@/pages/VideoCallPage";

// Use environment variable for preview, or current origin for production
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize mobile features
  useEffect(() => {
    initializeMobile();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("expertbase_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("expertbase_token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("expertbase_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, company = null, userType = "user") => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      company,
      user_type: userType,
    });
    const { token, user: userData } = response.data;
    localStorage.setItem("expertbase_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("expertbase_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Protected Route — guards /admin/dashboard from unauthenticated access
const AdminProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('expertbase_admin_token');
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

// App Router - Handles session_id detection for Google OAuth
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment synchronously during render for session_id
  // Using window.location.hash directly because useLocation().hash might not be updated on initial load
  // This prevents race conditions by processing Google OAuth callback FIRST
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    console.log("AppRouter: Detected session_id in URL, rendering AuthCallback");
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/experts" element={<ExpertsPage />} />
      <Route path="/experts/:id" element={<ExpertProfilePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      <Route path="/become-expert" element={<BecomeExpertPage />} />
      <Route
        path="/expert-onboarding-success"
        element={
          <ProtectedRoute>
            <ExpertOnboardingSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert-dashboard"
        element={
          <ProtectedRoute>
            <ExpertDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expert/edit-profile"
        element={
          <ProtectedRoute>
            <ExpertEditProfilePage />
          </ProtectedRoute>
        }
      />
      <Route path="/booking/success" element={<BookingSuccessPage />} />
      <Route
        path="/meeting/:bookingId"
        element={
          <ProtectedRoute>
            <VideoCallPage />
          </ProtectedRoute>
        }
      />
      
      {/* Legal & Info Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />

      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  // Seed data once on first-ever load (checks sessionStorage to avoid repeat calls)
  useEffect(() => {
    const alreadySeeded = sessionStorage.getItem("expertbase_seeded");
    if (alreadySeeded) return;
    
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
        sessionStorage.setItem("expertbase_seeded", "1");
      } catch (error) {
        // Ignore if already seeded
      }
    };
    seedData();
  }, []);

  return (
    <AuthProvider>
      <LocationProvider>
        <div className="App dark">
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#0B101B',
                border: '1px solid #1E293B',
                color: '#F8FAFC',
              },
            }}
          />
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </div>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;
